import { useEffect, useState } from "react";
import { useLocation, useRoute } from "wouter";
import {
  ArrowLeft,
  Edit,
  ExternalLink,
  Pause,
  Play,
  Share2,
  UserCheck,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { AuthWrapper } from "@/lib/auth-wrapper";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { JobDetailPresentation } from "@/components/jobs/JobDetailPresentation";
import { jobPostingsService, type JobPosting } from "@/lib/services";

const getStatusColor = (status: string) => {
  switch (status) {
    case "open":
      return "bg-green-500/20 text-green-400 border-green-500/30";
    case "closed":
    case "paused":
      return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
    case "filled":
      return "bg-blue-500/20 text-blue-400 border-blue-500/30";
    case "expired":
      return "bg-red-500/20 text-red-400 border-red-500/30";
    default:
      return "bg-gray-500/20 text-gray-400 border-gray-500/30";
  }
};

export default function JobManagePage() {
  const [, setLocation] = useLocation();
  const routeResult = useRoute("/jobs/:id/manage");
  const params = routeResult[1];
  const jobId = params?.id ?? "";

  const [job, setJob] = useState<JobPosting | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (jobId) {
      void loadJob();
    }
  }, [jobId]);

  const loadJob = async () => {
    if (!jobId) return;
    try {
      setLoading(true);
      const jobData = await jobPostingsService.getJob(jobId);
      setJob(jobData);
    } catch (error) {
      console.error("Error loading job:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleShareJob = async () => {
    try {
      const publicJobUrl = `${window.location.origin}/jobs/${jobId}/public`;
      await navigator.clipboard.writeText(publicJobUrl);
      toast.success("Job link copied to clipboard!");
    } catch (error) {
      console.error("Failed to copy link:", error);
      toast.error("Failed to copy link");
    }
  };

  const handleStatusToggle = async () => {
    try {
      const newStatus = job.status === "open" ? "closed" : "open";
      await jobPostingsService.updateJob(job.id!, { status: newStatus });
      await loadJob();
      toast.success(newStatus === "closed" ? "Job closed" : "Job reopened");
    } catch (error) {
      console.error("Error updating job status:", error);
      toast.error("Failed to update job status");
    }
  };

  const handleFilledToggle = async () => {
    try {
      const newStatus = job.status === "filled" ? "open" : "filled";
      await jobPostingsService.updateJob(job.id!, { status: newStatus });
      await loadJob();
      toast.success(newStatus === "filled" ? "Job marked as filled" : "Job reopened");
    } catch (error) {
      console.error("Error updating job status:", error);
      toast.error("Failed to update job status");
    }
  };

  if (loading) {
    return (
      <AuthWrapper>
        <div className="flex min-h-screen items-center justify-center bg-gray-900">
          <div className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-cyan-600" />
            <p className="text-gray-400">Loading job details...</p>
          </div>
        </div>
      </AuthWrapper>
    );
  }

  if (!job) {
    return (
      <AuthWrapper>
        <div className="flex min-h-screen items-center justify-center bg-gray-900">
          <div className="text-center">
            <h2 className="mb-2 text-xl font-semibold text-white">Job Not Found</h2>
            <p className="mb-4 text-gray-400">The job you're looking for doesn't exist.</p>
            <Button onClick={() => setLocation("/my-jobs")} className="bg-cyan-600 hover:bg-cyan-700">
              Back to My Jobs
            </Button>
          </div>
        </div>
      </AuthWrapper>
    );
  }

  return (
    <AuthWrapper>
      <div className="min-h-screen bg-gray-900">
        <div className="container mx-auto px-4 py-8">
          <div className="mx-auto max-w-6xl space-y-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <Button variant="ghost" onClick={() => setLocation("/my-jobs")} className="w-fit text-gray-300 hover:text-white">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to My Jobs
              </Button>

              <div className="flex flex-wrap items-center gap-3">
                <Badge className={`border ${getStatusColor(job.status)}`}>{job.status}</Badge>
                <Button onClick={() => setLocation(`/jobs/${jobId}/applicants`)} className="bg-cyan-600 hover:bg-cyan-700">
                  <Users className="mr-2 h-4 w-4" />
                  View Applicants ({job.applicants || 0})
                </Button>
              </div>
            </div>

            <JobDetailPresentation
              job={job}
              primaryAction={{
                label: "Edit Job",
                onClick: () => setLocation(`/jobs/${jobId}/edit`),
                icon: Edit,
              }}
              secondaryAction={{
                label: "Share Job",
                onClick: handleShareJob,
                icon: Share2,
                variant: "secondary",
              }}
              extraActions={[
                {
                  label: "Preview Job",
                  onClick: () => window.open(`/jobs/${jobId}/public`, "_blank"),
                  icon: ExternalLink,
                  variant: "secondary",
                },
                {
                  label: job.status === "open" ? "Close Job" : "Reopen Job",
                  onClick: handleStatusToggle,
                  icon: job.status === "open" ? Pause : Play,
                  variant: "secondary",
                },
                {
                  label: job.status === "filled" ? "Mark Open" : "Mark Filled",
                  onClick: handleFilledToggle,
                  icon: UserCheck,
                  variant: "secondary",
                },
              ]}
              footer={
                <Card className="min-h-[206px] border-gray-700 bg-gray-800">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-semibold text-gray-300">Performance Metrics</CardTitle>
                    <CardDescription className="text-gray-400">Track how your posting is performing.</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                      <div className="rounded-2xl bg-gray-700/50 p-3 text-center">
                        <div className="text-xl font-bold text-white">{job.views || 0}</div>
                        <div className="mt-1 text-xs text-gray-400">Views</div>
                      </div>
                      <div className="rounded-2xl bg-gray-700/50 p-3 text-center">
                        <div className="text-xl font-bold text-cyan-400">{job.applicants || 0}</div>
                        <div className="mt-1 text-xs text-gray-400">Applications</div>
                      </div>
                      <div className="rounded-2xl bg-gray-700/50 p-3 text-center">
                        <div className="text-xl font-bold text-blue-400">{job.status === "filled" ? 1 : 0}</div>
                        <div className="mt-1 text-xs text-gray-400">Filled Status</div>
                      </div>
                      <div className="rounded-2xl bg-gray-700/50 p-3 text-center">
                        <div className="text-xl font-bold text-gray-200">{job.status === "closed" ? "Closed" : "Live"}</div>
                        <div className="mt-1 text-xs text-gray-400">Listing State</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              }
            />
          </div>
        </div>
      </div>
    </AuthWrapper>
  );
}
